import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import LinkIcon from '@mui/icons-material/Link';
import { rulesAPI } from '../../services/api';
import { useRules, permissionError } from '../../hooks/useRules';
import './RulesPermissions.css';

// Wizetok Rules & Permissions — same screen as ReachOut's, plus the
// "Use ReachOut rules for Wizetok" switch for tenants that have both products.

const SCOPE_ORDER = ['NONE', 'OWN', 'ASSIGNED', 'TEAM', 'ALL'];
const ROLE_HINTS = {
  ADMIN: 'Company admins',
  MANAGER: 'Hiring managers',
  RECRUITER: 'Recruiters',
  VIEWER: 'Read-only users',
};
const TEAM_ROLES = ['RECRUITER', 'VIEWER'];
const VISIBILITY_LABELS = { OWN: 'Own only', ROLE_BASED: 'Role based', COMPANY: 'Company wide' };
const ACCESS_LABELS = { EDITOR: 'Editor', VIEWER: 'Viewer' };

const rank = (s) => SCOPE_ORDER.indexOf(s);
const copy = (data) => ({ permissions: JSON.parse(JSON.stringify(data.permissions)), policies: { ...data.policies } });

function optionLabel(policy, value) {
  if (policy.key === 'data.visibility') return VISIBILITY_LABELS[value] || value;
  if (policy.key === 'assign.default_access') return ACCESS_LABELS[value] || value;
  return value;
}

function valueLabel(kind, value, scopes) {
  if (kind === 'policy-bool') return value === 'true' ? 'On' : 'Off';
  if (kind === 'scope') return scopes.find((s) => s.value === value)?.label || value;
  return String(value);
}

function Toggle({ on, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      className={`rp-toggle${on ? ' on' : ''}`}
      onClick={() => !disabled && onChange(!on)}
    >
      <span className="rp-toggle-knob" />
    </button>
  );
}

const ReachOutChip = () => (
  <span className="rp-chip linked" title="Follows ReachOut's rule while the link is on">
    <LinkIcon style={{ fontSize: 11 }} /> ReachOut
  </span>
);

/** "Use ReachOut rules for Wizetok" — only shown when the company has both products. */
function LinkCard({ link, busy, onChange }) {
  if (!link || (!link.available && !link.enabled)) return null;
  return (
    <section className="rp-card">
      <div className={`rp-link${link.enabled ? ' on' : ''}`}>
        <div>
          <div className="rp-link-title"><LinkIcon style={{ fontSize: 18 }} /> Use ReachOut rules for Wizetok</div>
          <div className="rp-policy-desc">
            {link.enabled
              ? 'On: rules that exist in both products (campaigns ↔ positions, candidates, data visibility, delete / export, users) follow ReachOut automatically and are read-only here. Wizetok-only rules (questions, tests, interviews) stay editable below.'
              : 'Your company has both ReachOut and Wizetok. Turn this on to apply ReachOut\'s Rules & Permissions to Wizetok too, so you only manage them in one place. Wizetok-only rules stay editable here.'}
          </div>
          {link.enabled && link.reachable === false && (
            <div className="rp-policy-hint">ReachOut can&apos;t be reached right now — Wizetok is using its last copy of the rules.</div>
          )}
          {!link.available && link.enabled && (
            <div className="rp-policy-hint">ReachOut is no longer enabled for your company. Turn this off to manage Wizetok&apos;s rules here.</div>
          )}
        </div>
        <div className="rp-policy-control">
          <Toggle on={!!link.enabled} disabled={!link.editable || busy} label="Use ReachOut rules for Wizetok" onChange={onChange} />
        </div>
      </div>
    </section>
  );
}

/** Company-wide (or team) switch row. */
function PolicyRow({ policy, value, companyValue, editable, teamMode, linked, onChange }) {
  const lockedByAdmin = teamMode && !policy.teamEditable;
  const followsReachOut = linked && !!policy.reachout;
  const disabled = !editable || lockedByAdmin || followsReachOut;
  let control;
  if (policy.type === 'BOOLEAN') {
    control = <Toggle on={value === 'true'} disabled={disabled} label={policy.label} onChange={(on) => onChange(on ? 'true' : 'false')} />;
  } else if (policy.type === 'CHOICE') {
    control = (
      <select className="rp-select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {policy.options.map((o) => <option key={o} value={o}>{optionLabel(policy, o)}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        className="rp-number"
        type="number"
        min={0}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(String(Math.max(0, parseInt(e.target.value || '0', 10) || 0)))}
      />
    );
  }
  return (
    <div className="rp-policy">
      <div className="rp-policy-text">
        <div className="rp-policy-label">
          {policy.label}
          {followsReachOut && <ReachOutChip />}
          {lockedByAdmin && !followsReachOut && <span className="rp-chip"><LockIcon style={{ fontSize: 10 }} /> Set by admin</span>}
          {teamMode && policy.teamEditable && value !== companyValue && <span className="rp-chip team">Team rule</span>}
        </div>
        <div className="rp-policy-desc">{policy.description}</div>
        {teamMode && policy.teamEditable && !followsReachOut && (
          <div className="rp-policy-hint">Company setting: {policy.type === 'BOOLEAN' ? (companyValue === 'true' ? 'On' : 'Off') : optionLabel(policy, companyValue)}. Teams can only make this stricter.</div>
        )}
      </div>
      <div className="rp-policy-control">{control}</div>
    </div>
  );
}

/** One permission cell: checkbox for on/off actions, scope dropdown for campaign actions. */
function PermissionCell({ perm, role, value, original, ceiling, editable, scopes, onChange }) {
  const changed = value !== original;
  const locked = perm.lockedFor.includes(role);
  const why = locked ? (role === 'ADMIN' ? 'Admins always keep this' : 'Viewers are read-only in Wizetok') : undefined;
  if (!perm.positionScoped) {
    const allowedToTurnOn = !ceiling || ceiling === 'ALL';
    return (
      <td className={`rp-cell${changed ? ' changed' : ''}`}>
        <label className="rp-check" title={why}>
          <input
            type="checkbox"
            checked={value === 'ALL'}
            disabled={!editable || locked || (value !== 'ALL' && !allowedToTurnOn)}
            onChange={(e) => onChange(e.target.checked ? 'ALL' : 'NONE')}
          />
          <span className="rp-check-box" />
        </label>
      </td>
    );
  }
  const options = scopes.filter((s) => !ceiling || rank(s.value) <= rank(ceiling));
  return (
    <td className={`rp-cell${changed ? ' changed' : ''}`} title={why}>
      <select
        className={`rp-scope s-${value.toLowerCase()}`}
        value={value}
        disabled={!editable || locked}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </td>
  );
}

function ReviewModal({ changes, saving, onCancel, onConfirm }) {
  return (
    <div className="rp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="rp-modal">
        <h3>Review {changes.length} change{changes.length === 1 ? '' : 's'}</h3>
        <p className="rp-muted">These apply as soon as you save. Everyone affected sees the new rules on their next page load.</p>
        <ul className="rp-change-list">
          {changes.map((c) => (
            <li key={c.id}>
              <span className="rp-change-what">{c.what}</span>
              <span className="rp-change-from">{c.from}</span>
              <span className="rp-arrow">→</span>
              <span className="rp-change-to">{c.to}</span>
            </li>
          ))}
        </ul>
        <div className="rp-modal-actions">
          <button type="button" className="rp-btn ghost" onClick={onCancel} disabled={saving}>Back</button>
          <button type="button" className="rp-btn primary" onClick={onConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RulesPermissions() {
  const rules = useRules();
  const isAdmin = rules.canEditCompanyRules;
  const isManager = rules.hasRole('MANAGER');

  const [catalog, setCatalog] = useState(null);
  const [mode, setMode] = useState(isAdmin ? 'company' : 'team');
  const [managers, setManagers] = useState([]);
  const [teamOf, setTeamOf] = useState('');
  const [server, setServer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (rules.loaded) setMode(rules.canEditCompanyRules ? 'company' : 'team');
  }, [rules.loaded, rules.canEditCompanyRules]);

  useEffect(() => {
    rulesAPI.catalog().then((res) => setCatalog(res.data)).catch(() => setError('Could not load the rules catalog.'));
    rulesAPI.link().then((res) => setLink(res.data)).catch(() => setLink(null));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    rulesAPI.managers().then((res) => setManagers(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, [isAdmin]);

  const teamTarget = mode === 'team' ? (isAdmin ? teamOf : rules.email) : null;

  const load = useCallback(async () => {
    if (mode === 'team' && !teamTarget) {
      setServer(null);
      setDraft(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = mode === 'company' ? await rulesAPI.company() : await rulesAPI.team(teamTarget);
      setServer(res.data);
      setDraft(copy(res.data));
    } catch (err) {
      setError(permissionError(err, 'Could not load rules.'));
      setServer(null);
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [mode, teamTarget]);

  useEffect(() => { load(); }, [load]);

  const teamMode = mode === 'team';
  const editable = !!server?.editable;
  const linked = !!server?.linked;
  const scopes = useMemo(() => catalog?.scopes || [], [catalog]);

  const changes = useMemo(() => {
    if (!server || !draft || !catalog) return [];
    const out = [];
    catalog.policies.forEach((p) => {
      const before = server.policies[p.key];
      const after = draft.policies[p.key];
      if (before !== after) {
        const kind = p.type === 'BOOLEAN' ? 'policy-bool' : 'policy';
        out.push({
          id: `p:${p.key}`, type: 'policy', key: p.key, value: after,
          what: p.label,
          from: p.type === 'CHOICE' ? optionLabel(p, before) : valueLabel(kind, before, scopes),
          to: p.type === 'CHOICE' ? optionLabel(p, after) : valueLabel(kind, after, scopes),
        });
      }
    });
    catalog.permissions.forEach((p) => {
      catalog.roles.forEach((r) => {
        const before = server.permissions[p.key]?.[r.value];
        const after = draft.permissions[p.key]?.[r.value];
        if (before !== after) {
          out.push({
            id: `x:${p.key}:${r.value}`, type: 'permission', key: p.key, role: r.value, value: after,
            what: `${r.label} · ${p.label}`,
            from: p.positionScoped ? valueLabel('scope', before, scopes) : (before === 'ALL' ? 'Allowed' : 'Not allowed'),
            to: p.positionScoped ? valueLabel('scope', after, scopes) : (after === 'ALL' ? 'Allowed' : 'Not allowed'),
          });
        }
      });
    });
    return out;
  }, [server, draft, catalog, scopes]);

  const setPolicy = (key, value) => setDraft((d) => ({ ...d, policies: { ...d.policies, [key]: value } }));
  const setPerm = (key, role, value) => setDraft((d) => ({
    ...d,
    permissions: { ...d.permissions, [key]: { ...d.permissions[key], [role]: value } },
  }));

  const flash = (text) => { setError(''); setNotice(text); window.setTimeout(() => setNotice(''), 4000); };

  const save = async () => {
    const body = { permissions: {}, policies: {} };
    changes.forEach((c) => {
      if (c.type === 'policy') body.policies[c.key] = c.value;
      else body.permissions[c.key] = { ...(body.permissions[c.key] || {}), [c.role]: c.value };
    });
    setSaving(true);
    try {
      const res = mode === 'company' ? await rulesAPI.saveCompany(body) : await rulesAPI.saveTeam(teamTarget, body);
      setServer(res.data);
      setDraft(copy(res.data));
      setReviewing(false);
      flash(`Saved ${changes.length} change${changes.length === 1 ? '' : 's'}.`);
      rules.refresh();
    } catch (err) {
      setError(permissionError(err, 'Could not save rules.'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const what = teamMode ? 'this team\'s rules back to the company rules' : 'every company rule back to the Wizetok defaults';
    const keep = linked ? ' Rules that follow ReachOut are not affected.' : '';
    if (!window.confirm(`Reset ${what}?${keep} This cannot be undone (the audit log keeps a record).`)) return;
    setSaving(true);
    try {
      const res = mode === 'company' ? await rulesAPI.resetCompany() : await rulesAPI.resetTeam(teamTarget);
      setServer(res.data);
      setDraft(copy(res.data));
      flash('Rules reset.');
      rules.refresh();
    } catch (err) {
      setError(permissionError(err, 'Could not reset rules.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleLink = async (on) => {
    const question = on
      ? 'Use ReachOut\'s Rules & Permissions for Wizetok? Shared rules will follow ReachOut from now on and become read-only here.'
      : 'Stop using ReachOut\'s rules? Wizetok keeps a copy of the current values and you can change them here again.';
    if (!window.confirm(question)) return;
    setSaving(true);
    try {
      const res = await rulesAPI.setLink(on);
      setLink(res.data);
      flash(on ? 'Wizetok now follows ReachOut\'s rules.' : 'Wizetok rules are managed here again.');
      await load();
      rules.refresh();
    } catch (err) {
      setError(permissionError(err, 'Could not change the ReachOut link.'));
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    const map = new Map();
    catalog.permissions.forEach((p) => {
      if (q && !`${p.label} ${p.description} ${p.group}`.toLowerCase().includes(q)) return;
      if (onlyChanged && draft && server) {
        const changedHere = catalog.roles.some((r) => draft.permissions[p.key]?.[r.value] !== p.defaults[r.value]);
        if (!changedHere) return;
      }
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group).push(p);
    });
    return [...map.entries()];
  }, [catalog, search, onlyChanged, draft, server]);

  if (!rules.loaded || (!catalog && !error)) return <div className="rp-empty">Loading rules…</div>;

  if (!isAdmin && !isManager) {
    return <div className="rp-empty">Only admins and managers can open Rules &amp; Permissions.</div>;
  }

  return (
    <div className="rp">
      <div className="rp-head">
        <div>
          <h2><VerifiedUserIcon style={{ fontSize: 20 }} /> Rules &amp; Permissions</h2>
          <p className="rp-muted">
            {teamMode
              ? 'Rules for your team: the recruiters and viewers you (or they) invited. Team rules can only be stricter than the company rules.'
              : 'Decide what each role can do in Wizetok across the company. Defaults match how Wizetok worked before these rules existed.'}
          </p>
        </div>
        <div className="rp-head-actions">
          <div className="rp-seg" role="tablist">
            {isAdmin && (
              <button type="button" role="tab" aria-selected={mode === 'company'} className={mode === 'company' ? 'on' : ''} onClick={() => setMode('company')}>
                Company rules
              </button>
            )}
            {(isManager || isAdmin) && (
              <button type="button" role="tab" aria-selected={mode === 'team'} className={mode === 'team' ? 'on' : ''} onClick={() => setMode('team')}>
                {isAdmin ? 'Team rules' : 'My team\'s rules'}
              </button>
            )}
          </div>
          {teamMode && isAdmin && (
            <select className="rp-select" value={teamOf} onChange={(e) => setTeamOf(e.target.value)}>
              <option value="">Choose a manager…</option>
              {isManager && rules.email && <option value={rules.email}>My team</option>}
              {managers.filter((m) => m.email !== rules.email).map((m) => (
                <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && <div className="rp-warn">{error}</div>}
      {notice && <div className="rp-notice">{notice}</div>}

      {!teamMode && <LinkCard link={link} busy={saving} onChange={toggleLink} />}

      {teamMode && !teamTarget && <div className="rp-empty">Choose a manager to see or change their team&apos;s rules.</div>}
      {loading && (mode === 'company' || teamTarget) && <div className="rp-empty">Loading…</div>}

      {!loading && server && draft && catalog && (
        <>
          {!editable && <div className="rp-banner">You can view these rules. Only an admin can change company rules.</div>}
          {linked && editable && (
            <div className="rp-banner">
              Rows marked <ReachOutChip /> follow ReachOut. Change them in ReachOut&apos;s Rules &amp; Permissions, or turn off &quot;Use ReachOut rules for Wizetok&quot;.
            </div>
          )}

          <section className="rp-card">
            <div className="rp-card-head">
              <h3>{teamMode ? 'Team rules' : 'Company-wide rules'}</h3>
              <span className="rp-muted">Switches that apply to everyone{teamMode ? ' on this team' : ''}</span>
            </div>
            {catalog.policies.map((p) => (
              <PolicyRow
                key={p.key}
                policy={p}
                value={draft.policies[p.key]}
                companyValue={server.companyPolicies ? server.companyPolicies[p.key] : server.policies[p.key]}
                editable={editable}
                teamMode={teamMode}
                linked={linked}
                onChange={(v) => setPolicy(p.key, v)}
              />
            ))}
          </section>

          <section className="rp-card">
            <div className="rp-card-head">
              <h3>Role permissions</h3>
              <span className="rp-muted">
                People with several roles get everything any of their roles allows. Viewers are always read-only.
                {teamMode && ' Only the Recruiter and Viewer columns can be changed for a team.'}
              </span>
            </div>
            <div className="rp-toolbar">
              <div className="rp-search">
                <SearchIcon style={{ fontSize: 14 }} />
                <input placeholder="Search actions…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <label className="rp-inline">
                <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
                Only show actions that differ from the defaults
              </label>
            </div>
            <div className="rp-legend">
              <span><b>Own campaigns</b>: campaigns they created</span>
              <span><b>Own + assigned</b>: plus campaigns they are assigned to as Editor</span>
              <span><b>Team campaigns</b>: plus campaigns created by people they invited</span>
              <span><b>All campaigns</b>: every campaign in the company</span>
            </div>
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th className="rp-col-action">Action</th>
                    {catalog.roles.map((r) => (
                      <th key={r.value} className={teamMode && !TEAM_ROLES.includes(r.value) ? 'dim' : ''}>
                        <div>{r.label}</div>
                        <div className="rp-th-hint">{ROLE_HINTS[r.value]}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                {groups.map(([group, items]) => (
                  <tbody key={group}>
                    <tr className="rp-group-row" onClick={() => setCollapsed((c) => ({ ...c, [group]: !c[group] }))}>
                      <td colSpan={1 + catalog.roles.length}>
                        <span className="rp-group-label" aria-expanded={!collapsed[group]}>
                          {collapsed[group] ? <ChevronRightIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
                          <span>{group}</span>
                          <span className="rp-count">{items.length}</span>
                        </span>
                      </td>
                    </tr>
                    {!collapsed[group] && items.map((p) => {
                      const followsReachOut = linked && !!p.reachout;
                      return (
                        <tr key={p.key} className={followsReachOut ? 'rp-row-linked' : ''}>
                          <td className="rp-col-action">
                            <div className="rp-action-label">{p.label} {followsReachOut && <ReachOutChip />}</div>
                            <div className="rp-action-desc">{p.description}</div>
                          </td>
                          {catalog.roles.map((r) => {
                            const roleEditable = editable && !followsReachOut && (!teamMode || TEAM_ROLES.includes(r.value));
                            const ceiling = teamMode && server.companyPermissions ? server.companyPermissions[p.key][r.value] : null;
                            return (
                              <PermissionCell
                                key={r.value}
                                perm={p}
                                role={r.value}
                                value={draft.permissions[p.key][r.value]}
                                original={server.permissions[p.key][r.value]}
                                ceiling={ceiling}
                                editable={roleEditable}
                                scopes={scopes}
                                onChange={(v) => setPerm(p.key, r.value, v)}
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
              {groups.length === 0 && <div className="rp-empty small">No actions match.</div>}
            </div>
          </section>

          {editable && (
            <div className="rp-footer">
              <button type="button" className="rp-btn ghost" onClick={reset} disabled={saving}>
                <RestartAltIcon style={{ fontSize: 14 }} /> {teamMode ? 'Reset to company rules' : 'Reset to defaults'}
              </button>
            </div>
          )}

          {editable && changes.length > 0 && (
            <div className="rp-savebar">
              <span><b>{changes.length}</b> unsaved change{changes.length === 1 ? '' : 's'}</span>
              <div>
                <button type="button" className="rp-btn ghost" onClick={() => setDraft(copy(server))}>Discard</button>
                <button type="button" className="rp-btn primary" onClick={() => setReviewing(true)}>Review &amp; save</button>
              </div>
            </div>
          )}

          {reviewing && (
            <ReviewModal changes={changes} saving={saving} onCancel={() => setReviewing(false)} onConfirm={save} />
          )}
        </>
      )}
    </div>
  );
}
